import React from 'react';
import styled from 'styled-components';
import env from 'bench-environment';
import PropTypes from 'prop-types';
import MessageComponent from '@benchlabs/message-component';
import './message-styles.scss';

const PreWrapped = styled.div`
  white-space: pre-wrap;
`;

const urlRegex = /((?:https?:\/\/|www\.)\S*[^\s.;,(){}<>"])/g;

/**
 * Takes a string payload and returns an array containing the split chunks of
 * the payload, with each chunk containing a link being a React node
 * @param {string} payload
 * @returns {Array<String|ReactNode>}
 */
const linkifyPayload = payload => {
  const splitText = payload.split(urlRegex);
  const links = payload.match(urlRegex) || [];

  if (splitText.length <= 1) {
    return payload;
  }

  return splitText.map(item => {
    if (links.includes(item)) {
      return (
        <a key={Math.random()} href={item}>
          {item}
        </a>
      );
    }
    return item;
  });
};

/**
 * Given a payload and clientId, will replace any instance of a CWA url with a
 * clientId appended CWA url
 * @param {string} payload
 * @param {string} clientId
 * @returns {string} - Payload with CWA links amended to include clientId
 */
const preserveImpersonationForCwaUrls = (payload, clientId) => {
  const addClientIdToUrl = url => {
    const impersonationPath = `${env.location.cwa}/${clientId}`;
    if (url.includes(impersonationPath)) {
      return url;
    }
    return url.replace(env.location.cwa, impersonationPath);
  };
  return payload.replace(urlRegex, addClientIdToUrl);
};

/**
 * Given a payload and clientId, will auto-link any URLs, while preserving CWA
 * links with client IDs if the user is impersonating a client
 * @param {string} payload
 * @param {string} clientId
 * @returns {Array<string|ReactNode>}
 */
const formatLinks = (payload, clientId) => {
  const isUserImpersonatingClient = window.location.href.includes(
    `${env.location.cwa}/${clientId}`,
  );

  const linkPreservedPayload = isUserImpersonatingClient
    ? preserveImpersonationForCwaUrls(payload, clientId)
    : payload;

  return linkifyPayload(linkPreservedPayload);
};

/**
 * Accepts a clientId and message payload and performs any required presentation
 * transformations on the payload (i.e. impersonation links)
 * @param {Object} props
 * @property {string} props.clientId - the clientId corresponding to the message
 * @property {string} props.payload - The content of the message
 */
const MessagePayload = ({ clientId, payload, isNewMessageFormattingEnabled }) => {
  if (isNewMessageFormattingEnabled) {
    return <MessageComponent text={payload} className="message-component" />;
  }

  return <PreWrapped>{formatLinks(payload, clientId)}</PreWrapped>;
};

MessagePayload.propTypes = {
  clientId: PropTypes.string.isRequired,
  payload: PropTypes.string.isRequired,
  isNewMessageFormattingEnabled: PropTypes.bool,
};

export default MessagePayload;
